/*
  The Phase 2 deep-dive routes — per-system health, sales, messaging and DocuSign
  envelopes (see `frontend-api-contract.md`).

  One function per route, each returning the unwrapped `data` payload. They are
  thin on purpose: `getApi` already unwraps the envelope and normalises failures
  into `ApiError`, and reshaping for the UI happens in `lib/deep-dive-adapters.ts`.
  What lives here is the route vocabulary — paths, parameter names and the two
  places where a path segment and a platform name disagree.

  Every GET below is open. The two envelope actions need the Phase 1 JWT, which
  `deepDiveApi` attaches from storage.
*/

import { getApi, postApi, type QueryParams } from './caller'
import { deepDiveApi } from './client'
import type {
  DeepDiveService,
  EnvelopeActionResult,
  EnvelopeGroupBy,
  EnvelopeGroups,
  EnvelopesOverview,
  EnvelopesPage,
  EnvelopesQuery,
  FilterCatalogue,
  IncidentsPage,
  IncidentsQuery,
  MessagingAlerts,
  MessagingHourly,
  MessagingOverview,
  MessagingProvider,
  MessagingWindow,
  RejectionsPage,
  SalesFilterSelection,
  SalesOverview,
  SalesPeriodSelection,
  SalesSeries,
  SalesSummary,
  SalesPlatform,
  SalesQuarterlyPayload,
  SalesTimelinePayload,
  ServiceOverview,
  VendorStatusPayload,
} from './deep-dive.types'

/**
 * Path segment → platform name. One Portal is `one-portal` in a URL and
 * `one_portal` in `sales/filters?platform=` — the only place the two differ, and
 * a 400 `Invalid platform` if they are confused.
 */
export function salesFilterPlatform(service: DeepDiveService): SalesPlatform | null {
  if (service === 'one-portal') return 'one_portal'
  if (service === 'docusign') return null // DocuSign has health only, no sales.
  return service
}

/** Filter selection → repeatable query params, empty dimensions dropped. */
function filterParams(filters?: SalesFilterSelection): QueryParams {
  if (!filters) return {}
  const params: QueryParams = {}
  for (const [key, values] of Object.entries(filters)) {
    if (values && values.length) params[key] = values
  }
  return params
}

/* ------------------------------------------------------------------- health */

/** Status, response time, 30-day uptime and the open incident, if any. */
export async function getServiceOverview(service: DeepDiveService): Promise<ServiceOverview> {
  const { data } = await getApi<ServiceOverview>(deepDiveApi, `${service}/overview`)
  return data
}

/** Recorded status transitions, newest first. Defaults to a trailing 30 days. */
export async function getIncidents(
  service: DeepDiveService,
  query: IncidentsQuery = {},
): Promise<IncidentsPage> {
  const { data } = await getApi<IncidentsPage>(deepDiveApi, `${service}/incidents`, {
    from: query.from,
    to: query.to,
    limit: query.limit,
    offset: query.offset,
  })
  return data
}

/* -------------------------------------------------------------------- sales */

/** Today's count against the expected band, for one business day. */
export async function getSalesOverview(
  service: DeepDiveService,
  date?: string,
  filters?: SalesFilterSelection,
): Promise<SalesOverview> {
  const { data } = await getApi<SalesOverview>(deepDiveApi, `${service}/sales/overview`, {
    date,
    ...filterParams(filters),
  })
  return data
}

/** 24 hourly points for the day, actual and baseline, plain and cumulative. */
export async function getSalesTimeline(
  service: DeepDiveService,
  date?: string,
  filters?: SalesFilterSelection,
): Promise<SalesTimelinePayload> {
  const { data } = await getApi<SalesTimelinePayload>(deepDiveApi, `${service}/sales/timeline`, {
    date,
    ...filterParams(filters),
  })
  return data
}

/** Four quarters of counts, kW and contract value for one year. */
export async function getSalesQuarterly(
  service: DeepDiveService,
  year?: number,
  filters?: SalesFilterSelection,
): Promise<SalesQuarterlyPayload> {
  const { data } = await getApi<SalesQuarterlyPayload>(deepDiveApi, `${service}/sales/quarterly`, {
    year,
    ...filterParams(filters),
  })
  return data
}

/**
 * The upstream filter catalogue, passed through unchanged.
 *
 * The shape differs by platform (see `FilterCatalogue`), which is why this is one
 * route with a `platform` parameter rather than one per system.
 */
export async function getSalesFilters(platform: SalesPlatform): Promise<FilterCatalogue> {
  const { data } = await getApi<FilterCatalogue>(deepDiveApi, 'sales/filters', { platform })
  return data
}

/* ---------------------------------------------------------------- messaging */

/*
  Two route families, one shape: `sendgrid/messaging/*` is email and
  `twilio/messaging/*` is SMS. They are separate products from separate vendors
  with separate syncs, and are never summed.

  **No `channel` parameter.** Each family is bound to its own channel, and
  sending the other one (`twilio/messaging/overview?channel=email`) is a 400
  whose message names the right family. The parameter these routes used to take
  is gone; `provider` below picks the family instead, and `source` on every
  payload is what says which channel came back.

  A family whose vendor sync has not completed a pass answers 503 with the reason
  in `message` (the SMS half did exactly that on 2026-09-10, reporting a Twilio
  HTTP 401 / code 20003; prod answers "Twilio notifications not configured"
  today). That is a backend credential problem the client cannot retry around,
  which is why 503 sits in `retryDeepDive`'s terminal set.
*/

/** `sendgrid/messaging/hourly`, `twilio/messaging/alerts` — one family, one leaf. */
function messagingPath(provider: MessagingProvider, leaf: string): string {
  return `${provider}/messaging/${leaf}`
}

export async function getMessagingOverview(
  provider: MessagingProvider,
  window: MessagingWindow = '24h',
): Promise<MessagingOverview> {
  const { data } = await getApi<MessagingOverview>(
    deepDiveApi,
    messagingPath(provider, 'overview'),
    { window },
  )
  return data
}

export async function getMessagingHourly(
  provider: MessagingProvider,
  window: MessagingWindow = '24h',
): Promise<MessagingHourly> {
  const { data } = await getApi<MessagingHourly>(deepDiveApi, messagingPath(provider, 'hourly'), {
    window,
  })
  return data
}

/** One page of individual rejections. Pass a page's `next_cursor` for the next. */
export async function getMessagingRejections(
  provider: MessagingProvider,
  limit = 25,
  cursor?: string,
): Promise<RejectionsPage> {
  const { data } = await getApi<RejectionsPage>(
    deepDiveApi,
    messagingPath(provider, 'rejections'),
    { limit, cursor },
  )
  return data
}

/** The blockage inference, its evidence, and where to go and fix it. */
export async function getMessagingAlerts(
  provider: MessagingProvider,
): Promise<MessagingAlerts> {
  const { data } = await getApi<MessagingAlerts>(deepDiveApi, messagingPath(provider, 'alerts'))
  return data
}

/* ------------------------------------------------------------ vendor status */

/**
 * A vendor's own status page plus this deployment's account probe.
 *
 * This is the Phase 1 feed the Overview tiles poll (`{system}/status`), and it
 * exists for vendors with no health service of their own: SendGrid has no
 * `sendgrid/overview` and no `sendgrid/incidents`, so its uptime story is the
 * vendor's status page plus our own credential probe. Its *delivery* figures do
 * have a home of their own — `sendgrid/messaging/*`, alongside Twilio's.
 */
export async function getVendorStatus(vendor: string): Promise<VendorStatusPayload> {
  const { data } = await getApi<VendorStatusPayload>(deepDiveApi, `${vendor}/status`)
  return data
}

/* ---------------------------------------------------------------- envelopes */

/**
 * The period, for an envelope route.
 *
 * Same parameters as the sales version minus `compare`: these routes accept it
 * and then return no `previous`, so asking would advertise a comparison the
 * screen cannot draw. `window` is never sent alongside — the pair is mutually
 * exclusive upstream and both together is a 400.
 */
function envelopePeriodParams(selection: SalesPeriodSelection): QueryParams {
  const custom = selection.key === 'custom'
  return {
    period: selection.key,
    from: custom ? selection.from : undefined,
    to: custom ? selection.to : undefined,
  }
}

/*
  Shapes are final, data is not live yet: these answer 502/503 until the OWEHub
  e-sign API is deployed. That is a state the screen renders, not an error to
  swallow — see `dataSourceState` in `lib/deep-dive-adapters.ts`.
*/

export async function getEnvelopesOverview(
  selection: SalesPeriodSelection,
): Promise<EnvelopesOverview> {
  const { data } = await getApi<EnvelopesOverview>(deepDiveApi, 'docusign/envelopes/overview', {
    ...envelopePeriodParams(selection),
  })
  return data
}

export async function getEnvelopes(query: EnvelopesQuery = {}): Promise<EnvelopesPage> {
  const { data } = await getApi<EnvelopesPage>(deepDiveApi, 'docusign/envelopes', {
    ...(query.period ? envelopePeriodParams(query.period) : {}),
    status: query.status,
    office: query.office,
    finance_partner: query.finance_partner,
    search: query.search,
    limit: query.limit,
    cursor: query.cursor,
  })
  return data
}

export async function getEnvelopeGroups(
  groupBy: EnvelopeGroupBy = 'office',
  selection: SalesPeriodSelection,
): Promise<EnvelopeGroups> {
  const { data } = await getApi<EnvelopeGroups>(deepDiveApi, 'docusign/envelopes/by-group', {
    group_by: groupBy,
    ...envelopePeriodParams(selection),
  })
  return data
}

/**
 * Nudge every outstanding recipient on an envelope.
 *
 * JWT required (org_admin / platform_admin / management_user). A 409 means the
 * envelope was actioned recently — the thrown `ApiError` carries the backend's
 * wording and, in `detail`, the `cooldown_until` the button should wait for.
 */
export async function remindEnvelope(envelopeId: string): Promise<EnvelopeActionResult> {
  const { data } = await postApi<EnvelopeActionResult>(
    deepDiveApi,
    `docusign/envelopes/${encodeURIComponent(envelopeId)}/remind`,
  )
  return data
}

/** Send one recipient a fresh signing link. Same auth and cooldown rules. */
export async function resendEnvelopeLink(
  envelopeId: string,
  recipientId: string,
): Promise<EnvelopeActionResult> {
  const { data } = await postApi<EnvelopeActionResult>(
    deepDiveApi,
    `docusign/envelopes/${encodeURIComponent(envelopeId)}/recipients/${encodeURIComponent(recipientId)}/resend`,
  )
  return data
}

/* ----------------------------------------------------- period sales (2026-09) */

/**
 * Period selection → query params.
 *
 * `from`/`to` ride along only for `custom`; sending them otherwise is noise the
 * server ignores. `date=` is never sent — that is the old one-day API, and the
 * summary route quietly answers for today if it sees one, which would look like
 * a working filter that changes nothing.
 */
function periodParams(selection: SalesPeriodSelection): QueryParams {
  const custom = selection.key === 'custom'
  return {
    period: selection.key,
    from: custom ? selection.from : undefined,
    to: custom ? selection.to : undefined,
    // `previous` is the server default; sending it explicitly keeps the request
    // readable in a network log and survives a change of default.
    compare: selection.compare ?? 'previous',
  }
}

/**
 * Headline totals for a period, with the same span one step back.
 *
 * Replaces the fan-out this screen used to do — one call per day, because no
 * range parameter existed — with a single request the server aggregates.
 */
export async function getSalesSummary(
  service: DeepDiveService,
  selection: SalesPeriodSelection,
  filters?: SalesFilterSelection,
): Promise<SalesSummary> {
  const { data } = await getApi<SalesSummary>(deepDiveApi, `${service}/sales/summary`, {
    ...periodParams(selection),
    ...filterParams(filters),
  })
  return data
}

/**
 * The same period as a series of buckets.
 *
 * The bucket size is the server's decision (`period.granularity`) and arrives
 * pre-labelled; the client never derives either. A 9-month period plotted on a
 * 24-hour axis is the kind of chart that reads fine and means nothing.
 */
export async function getSalesSeries(
  service: DeepDiveService,
  selection: SalesPeriodSelection,
  filters?: SalesFilterSelection,
): Promise<SalesSeries> {
  const { data } = await getApi<SalesSeries>(deepDiveApi, `${service}/sales/series`, {
    ...periodParams(selection),
    ...filterParams(filters),
  })
  return data
}
